import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";

const coverage = [
  {
    icon: "ri-live-line",
    title: "影视资讯",
    desc: "整理最新剧集动态、上映消息与幕后花絮，帮助你把握影视圈的热点。",
  },
  {
    icon: "ri-book-open-line",
    title: "剧情介绍",
    desc: "提供清晰的剧情简介与看点提炼，让你在点开一集之前就能心里有数。",
  },
  {
    icon: "ri-user-star-line",
    title: "演员信息",
    desc: "汇总演员基本资料与代表作品，方便你按演员找作品、按作品认演员。",
  },
  {
    icon: "ri-list-check-2",
    title: "选片辅助",
    desc: "多维度筛选与排序，帮你从题材、地区、年份出发，挑出想看的内容。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们 - 我的老婆姐姐影视资讯",
    description:
      "了解我的老婆姐姐的网站定位、内容覆盖范围、联系方式与版权声明。我们专注影视剧资讯、剧情介绍与演员信息。",
    keywords: "关于我们,我的老婆姐姐,网站介绍,联系方式,版权声明",
  });

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <section className="mt-5 rounded-lg bg-primary-500 text-background-50 px-6 md:px-12 py-10 md:py-14">
        <h1 className="font-heading font-extrabold text-2xl md:text-4xl">关于「我的老婆姐姐」</h1>
        <p className="mt-4 max-w-3xl text-sm md:text-base text-primary-50/90 leading-relaxed">
          「我的老婆姐姐」是一个聚焦影视剧资讯与剧情内容的信息平台。我们面向喜欢追剧、
          关注影视动态的年轻观众与影迷，围绕最新影视资讯、剧情介绍和演员信息，
          提供清晰、易读、便于浏览的内容体验，帮助你在海量影视作品中快速了解、轻松选片。
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/category/tv"
            className="px-5 py-2.5 rounded-md bg-accent-500 text-secondary-950 font-label font-semibold text-sm hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            浏览电视剧
          </Link>
          <Link
            to="/category/movie"
            className="px-5 py-2.5 rounded-md border border-background-50/40 text-background-50 font-label font-medium text-sm hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
          >
            浏览电影
          </Link>
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 flex items-center gap-2 mb-5">
          <span className="w-1 h-5 md:h-6 rounded-full bg-accent-500"></span>
          内容覆盖范围
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {coverage.map((c) => (
            <div
              key={c.title}
              className="bg-background-50 border border-background-200 rounded-lg p-5"
            >
              <span className="w-11 h-11 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-700 text-xl mb-4">
                <i className={c.icon}></i>
              </span>
              <h3 className="font-heading font-semibold text-base text-foreground-950">
                {c.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-background-100 rounded-lg p-6 md:p-8">
          <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">联系方式</h2>
          <p className="text-sm text-foreground-600 leading-relaxed mb-5">
            如果你对我们的内容有建议、发现资料有误，或希望与我们合作，欢迎通过以下渠道联系。
          </p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                <i className="ri-mail-line"></i>
              </span>
              <a
                href="mailto:contact@wlpjj-demo.com"
                className="text-foreground-800 hover:text-primary-600 transition-colors"
              >
                contact@wlpjj-demo.com
              </a>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-weibo-line"></i>
              </span>
              <span className="text-foreground-800">微博 / @我的老婆姐姐</span>
            </li>
            <li className="flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-wechat-line"></i>
              </span>
              <span className="text-foreground-800">微信公众号 / 我的老婆姐姐</span>
            </li>
          </ul>
        </div>

        <div className="bg-background-50 border border-background-200 rounded-lg p-6 md:p-8">
          <h2 className="font-heading font-bold text-xl text-foreground-950 mb-4">使用说明与版权声明</h2>
          <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
            <p>
              本站所有影视、演员及相关资讯内容均为<strong className="font-medium text-foreground-800">演示性质的虚拟数据</strong>，
              用于展示网站的内容组织形式与浏览体验，不代表任何真实作品或个人。
            </p>
            <p>
              站内提供的搜索、筛选、收藏等功能均为前端演示逻辑，不接入真实数据接口；
              收藏记录仅保存在当前浏览器中，清除浏览器数据后将会丢失。
            </p>
            <p>
              如需引用本站内容，请注明来源。若你认为本站内容涉及权益问题，可通过上述联系方式与我们沟通，我们会及时处理。
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}